// Temperature trasporto — MOD 10 (T-251). Scheda di registro.
// Sorgente: public.food_transports (stessa tabella dell'export MOD 10).

import { confirmDialog } from '../../lib/ui/dialogs.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonInput,
  IonItem, IonModal, IonPage, IonSelect, IonSelectOption, IonSpinner,
  IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { addOutline } from 'ionicons/icons';
import { useState } from 'react';

import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useOfflineRegister, offlineRegisterStatus } from '../../lib/haccp/useOfflineRegister.js';

interface TRow {
  id: string;
  data_trasporto: string;
  destinazione: string;
  cliente_finale: string | null;
  alimento: string;
  tipo_alimento: string;
  t_partenza_c: number | null;
  t_arrivo_c: number | null;
  veicolo: string | null;
  operatore_nome: string | null;
  esito: string;
}

const TIPO: Array<[string, string]> = [['caldo', 'Caldo'], ['freddo', 'Freddo'], ['surgelato', 'Surgelato'], ['ambiente', 'Ambiente']];
const ESITO: Array<[string, string]> = [
  ['ok', 'OK'], ['t_fuori_range', 'T fuori range'], ['contenitore_danneggiato', 'Contenitore danneggiato'],
  ['ritardo', 'Ritardo'], ['rifiuto_destinatario', 'Rifiuto destinatario'], ['altro', 'Altro'],
];

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return `${String(dt.getUTCDate()).padStart(2, '0')}/${String(dt.getUTCMonth() + 1).padStart(2, '0')}/${dt.getUTCFullYear()}`;
}
function labelOf(o: Array<[string, string]>, v: string): string { return o.find(([k]) => k === v)?.[1] ?? v; }
function fmtT(t: number | null): string { return t === null || t === undefined ? '—' : `${t} °C`; }

const EMPTY = {
  data_trasporto: todayIso(), destinazione: '', cliente_finale: '', alimento: '',
  tipo_alimento: 'caldo', t_partenza_c: '', t_arrivo_c: '', veicolo: '', operatore_nome: '', esito: 'ok',
};

export function TrasportoPage(): JSX.Element {
  const { profile } = useAuth();
  const canEdit = profile?.is_platform_owner === true || profile?.role === 'consulente' || profile?.role === 'ristoratore';
  const [toast, setToast] = useState<string | null>(null);

  const { register, form, setForm, open, start, close, save: saveRecord, saving, ready,
    hasDraft, hasDraftChanges, discard: discardRecord, draftStatus, draftError, retryDraft } = useOfflineRegister<TRow, typeof EMPTY>(
    'food_transports', EMPTY, 'data_trasporto', canEdit,
  );
  const { data: rows = [], isLoading: loading, refetch: load } = register;

  function numOrNull(s: string): number | null {
    const n = Number(s.replace(',', '.'));
    return s.trim() && Number.isFinite(n) ? n : null;
  }

  async function discard(): Promise<void> {
    const remove = async () => {
      try { await discardRecord(); }
      catch (error) { setToast('Impossibile scartare la bozza: ' + toUserMessage(error)); }
    };
    if (!hasDraftChanges) { await remove(); return; }
    if (await confirmDialog('I dati della bozza saranno eliminati da questo dispositivo.', {
      header: 'Scartare la bozza?', okText: 'Scarta bozza', danger: true,
    })) await remove();
  }

  async function save(): Promise<void> {
    if (!profile?.active_tenant_id) { setToast('Nessun ristorante attivo'); return; }
    if (!form.destinazione.trim()) { setToast('Indica la destinazione'); return; }
    if (!form.alimento.trim()) { setToast("Indica l'alimento"); return; }
    try {
      const saved = await saveRecord({
        data_trasporto: new Date(form.data_trasporto + 'T12:00:00Z').toISOString(),
        destinazione: form.destinazione.trim(),
        cliente_finale: form.cliente_finale.trim() || null,
        alimento: form.alimento.trim(),
        tipo_alimento: form.tipo_alimento,
        t_partenza_c: numOrNull(form.t_partenza_c),
        t_arrivo_c: numOrNull(form.t_arrivo_c),
        veicolo: form.veicolo.trim() || null,
        operatore_nome: form.operatore_nome.trim() || null,
        esito: form.esito,
      });
      if (saved) setToast('Salvato sul dispositivo. In attesa di sincronizzazione.');
    } catch (error) {
      setToast('Salvataggio non riuscito: ' + toUserMessage(error));
    }
  }

  return (
    <IonPage>
      <IonHeader><IonToolbar>
        <IonButtons slot="start"><IonBackButton defaultHref="/haccp/registri-compila" /></IonButtons>
        <IonTitle>Temperature trasporto</IonTitle>
      </IonToolbar></IonHeader>
      <IonContent className="ion-padding">
        <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
          Le registrazioni si salvano sul dispositivo, anche senza rete, e si sincronizzano quando la connessione è disponibile.
        </p>
        {draftError && !open && <div role="alert"><p>{draftError}</p><IonButton onClick={retryDraft}>Riprova bozza</IonButton></div>}
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>
          Registro MOD 10: temperatura partenza/arrivo dei trasporti (catering/delivery). L'export A4 è in Export registri.
        </p>
        {canEdit && (
          <IonButton expand="block" disabled={!ready || saving} onClick={start} style={{ '--border-radius': '12px', marginBottom: 12 } as React.CSSProperties}>
            <IonIcon icon={addOutline} slot="start" /> {hasDraft ? 'Riprendi bozza' : 'Registra trasporto'}
          </IonButton>
        )}
        {register.isError && rows.length > 0 && (
          <div role="status"><p>Lettura non riuscita: sono mostrati gli ultimi dati caricati.</p><IonButton onClick={() => { void load(); }}>Riprova</IonButton></div>
        )}
        {loading ? <div style={{ textAlign: 'center', padding: 24 }}><IonSpinner /></div>
          : (register.isPending || register.isError) && rows.length === 0 ? (
          <div role="status" style={{ padding: 16 }}>
            <p>Impossibile leggere il registro sul dispositivo. Riprova.</p>
            <IonButton onClick={() => { void load(); }}>Riprova</IonButton>
          </div>
        ) : rows.length === 0 ? <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Nessun trasporto disponibile su questo dispositivo.</p>
          : rows.map((r) => (
            <div key={r.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 2px rgba(15,23,42,.06)' }}>
                {offlineRegisterStatus(r) && <p role="status" style={{ fontSize: 12, color: r._syncStatus === 'rejected' ? '#b91c1c' : '#92400e', margin: '0 0 6px' }}>{offlineRegisterStatus(r)}</p>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <b style={{ fontSize: 14 }}>{r.alimento}</b>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: r.esito === 'ok' ? '#16a34a' : '#dc2626', padding: '2px 8px', borderRadius: 999 }}>{labelOf(ESITO, r.esito)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#334155', marginTop: 4 }}>{fmtDate(r.data_trasporto)} → {r.destinazione} · {labelOf(TIPO, r.tipo_alimento)}</div>
              <div style={{ fontSize: 12, color: '#334155', marginTop: 2 }}>T partenza {fmtT(r.t_partenza_c)} → arrivo {fmtT(r.t_arrivo_c)}{r.veicolo ? ` · ${r.veicolo}` : ''}</div>
            </div>
          ))}

        <IonModal isOpen={open} canDismiss={!saving} onDidDismiss={close}>
          <IonHeader><IonToolbar>
            <IonTitle>Nuovo trasporto</IonTitle>
            <IonButtons slot="end">
              <IonButton color="danger" disabled={saving || !ready} onClick={() => { void discard(); }}>Scarta bozza</IonButton>
              <IonButton disabled={saving} onClick={close}>Chiudi</IonButton>
            </IonButtons>
          </IonToolbar></IonHeader>
          <IonContent className="ion-padding">
            <p role="status" style={{ color: '#64748b', fontSize: 13 }}>
              {draftStatus}
            </p>
            {draftError && <div role="alert"><p>{draftError}</p><IonButton disabled={saving} onClick={retryDraft}>Riprova bozza</IonButton></div>}
            <IonItem><IonInput type="date" label="Data *" labelPlacement="stacked" value={form.data_trasporto} onIonInput={(e) => setForm((f) => ({ ...f, data_trasporto: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Destinazione *" labelPlacement="stacked" placeholder="Indirizzo/luogo consegna" value={form.destinazione} onIonInput={(e) => setForm((f) => ({ ...f, destinazione: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Cliente finale" labelPlacement="stacked" value={form.cliente_finale} onIonInput={(e) => setForm((f) => ({ ...f, cliente_finale: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Alimento *" labelPlacement="stacked" value={form.alimento} onIonInput={(e) => setForm((f) => ({ ...f, alimento: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Tipo alimento *" labelPlacement="stacked" value={form.tipo_alimento} onIonChange={(e) => setForm((f) => ({ ...f, tipo_alimento: String(e.detail.value) }))}>{TIPO.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonItem><IonInput type="number" inputmode="decimal" label="T partenza (°C)" labelPlacement="stacked" value={form.t_partenza_c} onIonInput={(e) => setForm((f) => ({ ...f, t_partenza_c: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput type="number" inputmode="decimal" label="T arrivo (°C)" labelPlacement="stacked" value={form.t_arrivo_c} onIonInput={(e) => setForm((f) => ({ ...f, t_arrivo_c: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Veicolo" labelPlacement="stacked" placeholder="Targa / mezzo" value={form.veicolo} onIonInput={(e) => setForm((f) => ({ ...f, veicolo: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonInput label="Operatore" labelPlacement="stacked" value={form.operatore_nome} onIonInput={(e) => setForm((f) => ({ ...f, operatore_nome: String(e.detail.value ?? '') }))} /></IonItem>
            <IonItem><IonSelect label="Esito *" labelPlacement="stacked" value={form.esito} onIonChange={(e) => setForm((f) => ({ ...f, esito: String(e.detail.value) }))}>{ESITO.map(([k, v]) => <IonSelectOption key={k} value={k}>{v}</IonSelectOption>)}</IonSelect></IonItem>
            <IonButton expand="block" disabled={saving || !ready} onClick={() => { void save(); }} style={{ marginTop: 16, '--border-radius': '12px' } as React.CSSProperties}>{saving ? <IonSpinner name="dots" /> : 'Salva'}</IonButton>
          </IonContent>
        </IonModal>
        <IonToast isOpen={!!toast} message={toast ?? ''} duration={2600} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
